import { useState } from "react";
import { validateParameterEntryValue } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import type { ParameterDetail } from "@/types/line-editor.types";
import { useRegisterParameterEntry } from "./hooks/useRegisterParameterEntry";

interface Props {
  parameter: ParameterDetail;
  machineId: string;
  lineId: string;
}

const INPUT_MODE: Partial<Record<ParameterDetail["category"], "decimal" | "numeric">> = {
  NUMERIC: "decimal",
  COUNTER: "numeric",
  DURATION: "decimal",
};

export function ManualEntryField({ parameter, machineId, lineId }: Props) {
  // Mutation própria por campo: pending/feedback independentes.
  const register = useRegisterParameterEntry(machineId, lineId);
  const isBoolean = parameter.category === "BOOLEAN";
  // BOOLEAN começa SEM seleção (placeholder) — evita registrar "Sim" sem escolha
  // deliberada do operador (review fix #5).
  const [raw, setRaw] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Em branco/sem seleção não registra nem apaga o último valor (edge case da spec).
  const empty = raw.trim() === "";

  async function handleSubmit() {
    if (empty || register.isPending) return;
    // Validação/normalização da MESMA fonte usada pelo service (shared-types).
    const result = validateParameterEntryValue(parameter.category, raw);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setError(null);
    try {
      await register.mutateAsync({ parameterId: parameter.id, value: result.value });
      toast.success(`${parameter.name} registrado.`);
      if (!isBoolean) setRaw("");
    } catch (err) {
      const m = getFriendlyErrorMessage(err);
      toast.error("Falha ao registrar.", { description: m });
    }
  }

  const fieldId = `me-${parameter.id}`;
  return (
    <form
      className="space-y-1"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <Label htmlFor={fieldId} className="text-xs">
        {parameter.name}
        {parameter.unit ? (
          <span className="font-normal text-muted-foreground"> ({parameter.unit})</span>
        ) : null}
      </Label>
      <div className="flex items-center gap-2">
        {isBoolean ? (
          <select
            id={fieldId}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="" disabled>
              Selecione…
            </option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
        ) : (
          <Input
            id={fieldId}
            value={raw}
            inputMode={INPUT_MODE[parameter.category]}
            placeholder={parameter.category === "TEXT" ? "Texto livre" : "0"}
            className="h-8 flex-1"
            onChange={(e) => {
              setRaw(e.target.value);
              if (error) setError(null);
            }}
          />
        )}
        <Button type="submit" size="sm" disabled={empty || register.isPending}>
          {register.isPending ? "…" : "Registrar"}
        </Button>
      </div>
      {error ? <p className="text-xs text-rd-danger-700">{error}</p> : null}
    </form>
  );
}
